import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import {
    EmptyValue,
    RecordCard,
    RecordTableToolbar,
    recordTablePagination,
} from '@client/components/_ui/display/RecordPrimitives';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, TableEmptyState, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Input } from 'antd';
import { useState } from 'react';
import { isConsolidatorRecord } from './ClientRecordHeader';
import { useClientRecord } from './client-record-data';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/details/$id/clients')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClientsTab />
        </PageController>
    );
}

type ClientQueryParams = RouterInputs['fleetClientDb']['listFleetClients'];
type ClientRow = NonNullable<RouterOutputs['fleetClientDb']['listFleetClients']>['rows'][number];
const columnHelper = createColumnHelper<ClientRow, NonNullable<ClientQueryParams['orderBy']>>();

const DETAILS_ROUTE = '/console/$orgId/configuration/fleet/client-database/details/$id';

/** The consolidator's clients: the same rows as the client database list, scoped to this parent. */
function ClientsTab() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { id } = Route.useParams();
    const { data: client } = useClientRecord(id);
    const [params, setParams] = useState<ClientQueryParams>({
        limit: 20,
        orderBy: { name: 'asc' },
        where: {},
    });
    const [searchText, setSearchText] = useState('');

    const isConsolidator = client ? isConsolidatorRecord(client) : false;
    const clientsQuery = trpc.fleetClientDb.listFleetClients.useQuery(
        isConsolidator ? { ...params, where: { ...params.where, parentId: id } } : skipToken,
    );

    const hasActiveFilters = !!params.where?.OR;

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: clientsQuery.data?.rows || [],
        total: clientsQuery.data?.total || 0,
        loading: clientsQuery.isLoading,
        queryParams: params,
        onParamsChange: setParams,
        pagination: recordTablePagination,
        emptyContent: (
            <TableEmptyState
                hasActiveFilters={hasActiveFilters}
                noRecordsTitle="No clients yet"
                noRecordsDescription="Clients join this consolidator when their record names it as the parent."
                noMatchesTitle="No clients match"
                noMatchesDescription="Try another name or client number, or clear the search."
            />
        ),
        columns: [
            columnHelper.accessor('clientNo', {
                id: 'clientNo',
                header: 'Client no',
                sortable: 'clientNo',
                width: 120,
                cell: ({ row }) => <span className="text-subtle-foreground tabular-nums">{row.clientNo}</span>,
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Client',
                sortable: 'name',
                cell: ({ row }) => <span className="font-semibold">{row.name}</span>,
            }),
            columnHelper.accessor('state', {
                id: 'state',
                header: 'State',
                sortable: 'state',
                width: 90,
                cell: ({ row }) => row.state || <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.accessor('vehiclesCount', {
                id: 'vehiclesCount',
                header: 'Vehicles',
                align: 'center',
                width: 90,
                cell: ({ row }) => <span className="tabular-nums">{row.vehiclesCount}</span>,
            }),
            columnHelper.accessor('driversCount', {
                id: 'driversCount',
                header: 'Drivers',
                align: 'center',
                width: 90,
                cell: ({ row }) => <span className="tabular-nums">{row.driversCount}</span>,
            }),
            columnHelper.accessor('accountManagerName', {
                id: 'accountManager',
                header: 'Account manager',
                cell: ({ row }) => row.accountManagerName || <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                width: 110,
                cell: ({ row }) => row.status || <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.actions({
                width: 40,
                cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button.OpenInNewTab to={DETAILS_ROUTE} params={{ orgId, id: row.id }} />
                    </div>
                ),
            }),
        ],
    });

    if (client && !isConsolidator) {
        return null;
    }

    return (
        <RecordCard padded={false}>
            <RecordTableToolbar>
                <Input.Search
                    placeholder="Search by name or client no"
                    allowClear
                    className="w-72"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onSearch={(value) => {
                        const term = value.trim();
                        setParams((prev) => ({
                            ...prev,
                            offset: 0,
                            where: {
                                ...prev.where,
                                OR: term
                                    ? [
                                          { name: { contains: term, mode: 'insensitive' } },
                                          { clientNo: { contains: term, mode: 'insensitive' } },
                                      ]
                                    : undefined,
                            },
                        }));
                    }}
                />
            </RecordTableToolbar>
            {clientsQuery.error ? (
                <div className="p-4">
                    <InlineQueryError compact error={clientsQuery.error} onRetry={() => void clientsQuery.refetch()} />
                </div>
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={({ id: childId }) => ({
                        className: 'cursor-pointer',
                        onClick(event) {
                            navigate({ to: DETAILS_ROUTE, params: { orgId, id: childId } }, event);
                        },
                    })}
                />
            )}
        </RecordCard>
    );
}
